/* eslint-disable import/no-unresolved */
import React, { useEffect, useState, useRef } from 'react';
import { useSetAtom, useAtomValue } from 'jotai';
import apptracker from '@utils/sensor/apptracker';
import { PROMPT_ANSWER } from '@src/utils/sensor/track';
import DashboardTabs from '@components/common/Dashboard/DashboardTabs';
import ChatThread from '@components/prompt/chat-thread/ChatThread';
import ChatThreadBgPlaceholder from '@components/prompt/chat-thread/ChatThreadBgPlaceholder';
import PopularQuestions from '@components/prompt/popular-questions/PopularQuestions';
import MessageField from '@components/prompt/message-field/MessageField';
import DisclaimerText from '@components/prompt/disclaimer-text/DisclaimerText';
import { chatFeedWriteAtom, lastChatAtom, chatFeedAtom } from '@components/prompt/atoms';
import {
  getBotAnswerText,
  botAnswerHasMetricData,
  botAnswerHasDimensionData,
} from '@components/prompt/utils';
import useGetStreamResponse from './hooks/useGetStreamResponse';
import {
  ContentWrapper,
  LeftSection,
  PromptWrapper,
  RightSection,
  RightSectionContent,
  LeftSectionHead,
  LeftSectionContent,
} from './styled';
import { LeftCircleOutlined, RightCircleOutlined } from '@ant-design/icons';
import ErrorBoundary from '@components/common/Retry/ErrorBoundary';
import ReleaseVersion from '@components/common/ReleaseVersion';

function Prompt() {
  const { isLoading, hasStreamingEnded, error, botData, getStream, queryStartTime } =
    useGetStreamResponse('conversation');
  const chatFeed = useAtomValue(chatFeedAtom);
  const updateLastChatBlock = useSetAtom(chatFeedWriteAtom);
  const lastChatObj = useAtomValue(lastChatAtom);
  const lastChatTs = useRef<null | number | undefined>(null);

  const [collapseLeft, setCollapseLeft] = useState(false);

  const isChatEmpty = chatFeed.length === 0;

  useEffect(() => {
    // if error set error for that chat
    if (error.title) {
      updateLastChatBlock({
        error,
        timestamp: Date.now(),
      });
    } else {
      // if no error set the response text
      // https://conviva.atlassian.net/browse/AAA-316
      // when switch back from another page url, the last response copy will be lost
      if (!lastChatObj.timestamp || botData?.length) {
        updateLastChatBlock({
          botData,
        });
      }
    }

    if (hasStreamingEnded) {
      updateLastChatBlock({
        timestamp: Date.now(),
      });
      const responseText = getBotAnswerText(botData);
      const timeCost = Date.now() - queryStartTime;
      apptracker(PROMPT_ANSWER, {
        questionAndAnswer: `${lastChatObj.userMsg}|||${responseText}`,
        hasMetric: botAnswerHasMetricData(botData) ? 'yes' : 'no',
        hasDimension: botAnswerHasDimensionData(botData) ? 'yes' : 'no',
        e2eTimeCost: timeCost,
        charactersPerSecond: (responseText.length / (timeCost / 1000)).toFixed(1),
      });
    }
  }, [updateLastChatBlock, hasStreamingEnded, error, lastChatObj.userMsg, botData]);

  // https://github.com/facebook/react/issues/14965
  // use a ref to get the latest value of the state
  useEffect(() => {
    lastChatTs.current = lastChatObj.timestamp;
  }, [lastChatObj]);

  const resizeHandler = () => {
    if (window.innerWidth <= 1400) {
      setCollapseLeft(true);
    }
  };

  useEffect(() => {
    resizeHandler();
    window.addEventListener('resize', resizeHandler);
    return () => {
      // https://conviva.atlassian.net/browse/AAA-607
      // before the page is unmounted, if the last question is receiving data
      // we set the latest question done before leave
      if (!lastChatTs.current) {
        updateLastChatBlock({
          timestamp: Date.now(),
        });
      }
      window.removeEventListener('resize', resizeHandler);
    };
  }, []);

  return (
    <PromptWrapper>
      <DashboardTabs />
      <ErrorBoundary>
        <ReleaseVersion type={'Alpha'} />
        <ContentWrapper>
          {!isChatEmpty ? (
            <LeftSection style={{ width: collapseLeft ? '24px' : '350px' }}>
              {collapseLeft ? (
                <RightCircleOutlined
                  style={{ color: '#0046FF', fontSize: '24px', cursor: 'pointer' }}
                  onClick={() => {
                    setCollapseLeft(!collapseLeft);
                  }}
                />
              ) : (
                <LeftSectionContent>
                  <LeftSectionHead>
                    {/* remove the 'Queries' content, feedback from Jens */}
                    <h3></h3>
                    <LeftCircleOutlined
                      style={{ color: '#0046FF', fontSize: '24px', cursor: 'pointer' }}
                      onClick={() => {
                        setCollapseLeft(!collapseLeft);
                      }}
                    />
                  </LeftSectionHead>
                  <PopularQuestions disabled={isLoading} getStream={getStream} />
                </LeftSectionContent>
              )}
            </LeftSection>
          ) : null}
          <RightSection
            style={{
              width: !isChatEmpty ? 'auto' : '100%',
              flex: !isChatEmpty ? '1' : 'initial',
              padding: !isChatEmpty ? '0 0 16px 0px' : '24px 64px',
              overflow: !isChatEmpty ? 'hidden' : 'auto',
            }}
          >
            <RightSectionContent>
              {isChatEmpty ? (
                <ChatThreadBgPlaceholder getStream={getStream} />
              ) : (
                <ChatThread getStream={getStream} isLoading={isLoading} />
              )}
              <MessageField
                style={{
                  width: !isChatEmpty ? '100%' : '1170px',
                  padding: !isChatEmpty ? '16px 100px' : '16px 0px',
                  margin: 'auto',
                }}
                disabled={isLoading}
                getStream={getStream}
              />
              <DisclaimerText
                style={{
                  width: !isChatEmpty ? '100%' : '1170px',
                  margin: 'auto',
                  flex: !isChatEmpty ? 'initial' : 1,
                }}
              />
            </RightSectionContent>
          </RightSection>
        </ContentWrapper>
      </ErrorBoundary>
    </PromptWrapper>
  );
}

export default Prompt;
